import React, { useCallback, useEffect } from 'react';

import { useStoreContext } from 'features/store';
import { useIntl } from 'react-intl';
import { View } from 'react-native';

import { Helmet } from 'components/helmet';
import { LayoutContainer } from 'components/layout/styled';
import { usePullToReload } from 'components/pull-to-reload/pull-to-reload';
import { ServiceMode } from 'generated/graphql-gateway';
import { useRoute } from 'hooks/navigation/use-route';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useMenuScroll } from 'hooks/use-menu-scroll';
import { useFocusEffect } from 'navigation/hooks/use-focus-effect';
import { MenuReorderWidget } from 'pages/menu/components/menu-reorder-widget';
import { MenuSection } from 'pages/menu/components/menu-section/menu-section';
import { MenuSkeletonLoader } from 'pages/menu/components/menu-tile-grid/menu-tile-grid-loading';
import withMenuEmptyState from 'pages/menu/components/with-menu-empty-state';
import { MenuEmptyState } from 'pages/menu/main-menu-page/menu-empty-state';
import { useMenuContext } from 'state/menu';
import { useServiceModeContext } from 'state/service-mode';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { EventName, emitEvent } from 'utils/event-hub';
import { waitForDuration } from 'utils/wait-for-duration';

// NEW one menu
const MainMenuPage = () => {
  useRumPageView('main-menu', 'Main Menu');
  useMenuScroll();

  const { mainMenu, isLoading, refetchMenu, rawMenu } = useUnifiedMenuContext();
  const { activeDayParts, showBurgersForBreakfast } = useMenuContext();
  const { serviceMode, setOrderBay } = useServiceModeContext();
  const { formatMessage } = useIntl();
  const isMobile = useIsMobileBreakpoint();
  const { store } = useStoreContext();

  const { params } = useRoute<{ identifier: string }>();

  useEffect(() => {
    // If an 'identifier' is present in this path and customer is on Curbside
    // service mode, assume the customer is in the Order Bay flow
    if (serviceMode === ServiceMode.CURBSIDE && params.identifier) {
      setOrderBay(params.identifier);
    }
  }, [serviceMode, setOrderBay, params.identifier]);

  const onRefresh = async () => {
    // This is just an animation feedback of one second
    await waitForDuration(1000);
    refetchMenu();
  };

  useFocusEffect(
    useCallback(() => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'menu',
      });
    }, [])
  );

  const pullToReloadProps = usePullToReload({
    onRefresh,
    isDisabled: isLoading,
  });

  if (isLoading) {
    return <MenuSkeletonLoader />;
  }

  return (
    <LayoutContainer isFullContainer={isMobile}>
      <Helmet title={formatMessage({ id: 'mainMenu' })} />
      <View style={{ flex: 1 }}>
        <MenuReorderWidget />
        {mainMenu.length > 0 ? (
          <MenuSection data={mainMenu} {...pullToReloadProps} />
        ) : (
          <MenuEmptyState
            logWhenTrue={!isLoading}
            logContext={{
              activeDayParts: activeDayParts.map(dayPart => dayPart.key),
              showBurgersForBreakfast,
              serviceMode,
              isFetchedMenuEmpty: !rawMenu || rawMenu.entries.length === 0,
              isPricingAndDependenciesSettled: true,
              storeId: store.number,
              storePosVendor: store.pos?.vendor,
            }}
          />
        )}
      </View>
    </LayoutContainer>
  );
};

// MainMenuPage.whyDidYouRender = true;
export const MainMenuPageV2 = withMenuEmptyState(MainMenuPage);
